import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/api/queryKeys';
import { campusesApi, schoolsApi } from '@/services/api/endpoints/schools';
import { academicYearsApi, gradesApi, subjectsApi } from '@/services/api/endpoints/academic';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';

/**
 * Reference data for dropdowns.
 *
 * Every form in the administration section needs some combination of schools,
 * campuses, academic years, grades and subjects. These hooks hit the dedicated
 * `/picker` endpoints — unpaginated and minimal — and share one cache entry
 * across every form that opens, rather than each re-fetching a full page.
 *
 * Each is permission-gated, so a user who cannot read campuses simply gets an
 * empty list instead of a failed request.
 */

const PICKER_OPTIONS = {
  // Reference data changes rarely; a long stale time keeps forms instant.
  staleTime: 5 * 60_000,
} as const;

export function useSchoolPicker() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.schools.picker,
    queryFn: schoolsApi.picker,
    enabled: can(PERMISSIONS.SCHOOL_VIEW),
    ...PICKER_OPTIONS,
  });
}

export function useCampusPicker() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.campuses.picker,
    queryFn: campusesApi.picker,
    enabled: can(PERMISSIONS.CAMPUS_VIEW),
    ...PICKER_OPTIONS,
  });
}

export function useAcademicYearPicker() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.academicYears.picker,
    queryFn: academicYearsApi.picker,
    enabled: can(PERMISSIONS.ACADEMIC_YEAR_VIEW),
    ...PICKER_OPTIONS,
  });
}

export function useGradePicker() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.grades.picker,
    queryFn: gradesApi.picker,
    enabled: can(PERMISSIONS.GRADE_VIEW),
    ...PICKER_OPTIONS,
  });
}

export function useSubjectPicker() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.subjects.picker,
    queryFn: subjectsApi.picker,
    enabled: can(PERMISSIONS.SUBJECT_VIEW),
    ...PICKER_OPTIONS,
  });
}
